import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiPlus, 
  FiFilter, 
  FiEdit2, 
  FiTrash2, 
  FiEye,
  FiRefreshCw,
  FiLayers,
  FiBook,
  FiCalendar,
  FiX,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiBookOpen,
  FiAward,
  FiLock,
  FiChevronDown,
  FiChevronUp,
  FiGrid,
  FiCreditCard,
  FiActivity,
  FiDatabase,
  FiTrendingUp,
  FiList // For terms icon
} from 'react-icons/fi';
import ProgramSubjectMapping from './ProgramSubjectMapping';
import ViewMappedSubjects from './ViewMappedSubjects';
import { useAcademicData } from './hooks/useAcademicData';
import api from '../../api';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';

const ProgramSubjectMappingSection = ({
  tenantId,
  programs,
  programTypes,
  selectedProgramTypeId,
}) => {
  console.log('🔍 ProgramSubjectMappingSection - Initial props:', {
    tenantId,
    selectedProgramTypeId,
    programsCount: programs?.length,
    programTypesCount: programTypes?.length,
  });

  // Academic context state
  const [selectedProgramType, setSelectedProgramType] = useState(selectedProgramTypeId || '');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);

  // UI state
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasExistingMapping, setHasExistingMapping] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [expandedMapping, setExpandedMapping] = useState(null);

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteMappingId, setDeleteMappingId] = useState(null);
  const [deleteMappingName, setDeleteMappingName] = useState('');
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');

  // For failproof deletion: type-to-confirm
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Store program and program type names for display
  const [programNamesMap, setProgramNamesMap] = useState({});
  const [programTypeNamesMap, setProgramTypeNamesMap] = useState({});
  const [showCompletedAYs, setShowCompletedAYs] = useState(false);

  // Track if selected AY is completed
  const [isSelectedAYCompleted, setIsSelectedAYCompleted] = useState(false);

  // Custom hooks
  const { 
    academicYears, 
    completedAcademicYears 
  } = useAcademicData(tenantId, false);

  // Fix program type comparison - handle both string and object types
  const normalizeProgramTypeId = (programTypeId) => {
    if (!programTypeId) return '';
    return typeof programTypeId === 'object' ? programTypeId._id : programTypeId;
  };

  // Filter programs based on selected program type
  const filteredPrograms = selectedProgramType 
    ? programs.filter(program => {
        const programTypeId = normalizeProgramTypeId(program.programTypeId);
        const normalizedSelectedType = normalizeProgramTypeId(selectedProgramType);
        return programTypeId === normalizedSelectedType;
      })
    : programs;

  // Get selected program details
  const selectedProgramDetails = programs.find(p => p._id === selectedProgram);

  // Get selected academic year details
  const selectedAcademicYearDetails = academicYears.find(ay => ay._id === selectedAcademicYear) || 
                                     completedAcademicYears.find(ay => ay._id === selectedAcademicYear);

  // Build program names map from programs prop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (programs && programs.length > 0) {
      const namesMap = {};
      programs.forEach(program => {
        if (program._id) {
          namesMap[program._id] = {
            name: program.name || 'Unknown Program',
            code: program.code || '',
            programTypeId: program.programTypeId
          };
        }
      });
      setProgramNamesMap(namesMap);
    }
  }, [programs]);

  // Build program type names map from programTypes prop
  useEffect(() => {
    if (programTypes && programTypes.length > 0) {
      const namesMap = {};
      programTypes.forEach(type => {
        if (type._id) {
          namesMap[type._id] = {
            name: type.name || 'Unknown Type',
            code: type.code || ''
          };
        }
      });
      setProgramTypeNamesMap(namesMap);
    }
  }, [programTypes]);

  // Check if selected academic year is completed
  useEffect(() => {
    if (selectedAcademicYear) {
      const isCompleted = completedAcademicYears.some(ay => ay._id === selectedAcademicYear);
      setIsSelectedAYCompleted(isCompleted);
    } else {
      setIsSelectedAYCompleted(false);
    }
  }, [selectedAcademicYear, completedAcademicYears]);

  // Get program name by ID with fallback
  const getProgramName = useCallback((programId) => {
    if (!programId) return '';
    
    if (typeof programId === 'object' && programId.name) {
      return programId.name;
    }
    
    const program = programNamesMap[programId];
    if (program) {
      return program.name;
    }
    
    const foundProgram = programs.find(p => p._id === programId);
    if (foundProgram) {
      return foundProgram.name;
    }
    
    return typeof programId === 'object' ? programId._id || 'Unknown Program' : programId;
  }, [programNamesMap, programs]);

  // Get program code by ID with fallback
  const getProgramCode = useCallback((programId) => {
    if (!programId) return '';
    
    if (typeof programId === 'object' && programId.code) {
      return programId.code;
    }
    
    const program = programNamesMap[programId];
    if (program) {
      return program.code;
    }
    
    const foundProgram = programs.find(p => p._id === programId);
    return foundProgram?.code || '';
  }, [programNamesMap, programs]);

  // Get program type name by ID with fallback
  const getProgramTypeName = useCallback((typeId) => {
    if (!typeId) return '';
    
    if (typeof typeId === 'object' && typeId.name) {
      return typeId.name;
    }
    
    const type = programTypeNamesMap[typeId];
    if (type) {
      return type.name;
    }
    
    const foundType = programTypes.find(pt => pt._id === typeId);
    if (foundType) {
      return foundType.name;
    }
    
    return typeof typeId === 'object' ? typeId._id || 'Unknown Type' : typeId;
  }, [programTypeNamesMap, programTypes]);

  // Get program type code by ID with fallback
  const getProgramTypeCode = useCallback((typeId) => {
    if (!typeId) return '';
    
    if (typeof typeId === 'object' && typeId.code) {
      return typeId.code;
    }
    
    const type = programTypeNamesMap[typeId];
    if (type) {
      return type.code;
    }
    
    const foundType = programTypes.find(pt => pt._id === typeId);
    return foundType?.code || '';
  }, [programTypeNamesMap, programTypes]);

  // Get semester name by ID
  const getSemesterName = (semesterId) => {
    if (!semesterId) return '';
    if (typeof semesterId === 'object') {
      return semesterId.semesterName || `Semester ${semesterId.semesterNumber}`;
    }
    const found = availableSemesters.find(sem => sem._id === semesterId);
    return found ? (found.semesterName || `Semester ${found.semesterNumber}`) : '';
  };

  // Get term name by ID
  const getTermName = (termId) => {
    if (!termId) return '';
    if (typeof termId === 'object') {
      return termId.termLabel || `Term ${termId.termNumber}`;
    }
    const found = availableTerms.find(term => term._id === termId);
    return found ? (found.termLabel || `Term ${found.termNumber}`) : '';
  };

  // Get academic year name by ID
  const getAcademicYearName = (id) => {
    if (!id) return '';
    if (typeof id === 'object' && id.name) return id.name;
    const found = academicYears.find(ay => ay._id === id) || 
                 completedAcademicYears.find(ay => ay._id === id);
    return found ? found.name : '';
  };

  // Initialize selectedProgramType from props when it changes
  useEffect(() => {
    if (selectedProgramTypeId && selectedProgramTypeId !== selectedProgramType) {
      setSelectedProgramType(selectedProgramTypeId);
      setSelectedProgram('');
      setSelectedAcademicYear('');
      setSelectedSemester('');
      setSelectedTerm('');
    }
  }, [selectedProgramTypeId, selectedProgramType]);

  // Fetch semesters or terms when academic year changes
  useEffect(() => {
    const fetchSemestersOrTermsForAcademicYear = async () => {
      if (!selectedAcademicYear) {
        setAvailableSemesters([]);
        setAvailableTerms([]);
        setSelectedSemester('');
        setSelectedTerm('');
        setIsSemesterBased(false);
        return;
      }

      try {
        const selectedAY = academicYears.find(ay => ay._id === selectedAcademicYear) || 
                          completedAcademicYears.find(ay => ay._id === selectedAcademicYear);
        
        if (selectedAY) {
          const semesterBased = selectedAY.isSemesterBased || false;
          setIsSemesterBased(semesterBased);
          
          if (semesterBased) {
            // Fetch semesters for semester-based AY
            const token = localStorage.getItem('token');
            const res = await api.get(`/api/classManager/semesters/${selectedAcademicYear}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            
            const fetchedSemesters = res.data.semesters || [];
            setAvailableSemesters(fetchedSemesters);
            setAvailableTerms([]);
            
            if (fetchedSemesters.length > 0 && !selectedSemester) {
              setSelectedSemester(fetchedSemesters[0]._id);
            } else if (fetchedSemesters.length === 0) {
              setSelectedSemester('');
            }
            setSelectedTerm('');
          } else {
            // Fetch terms for non-semester-based AY
            const token = localStorage.getItem('token');
            const res = await api.get(`/api/academic-year/${selectedAcademicYear}/terms?tenantId=${tenantId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            
            const fetchedTerms = res.data?.terms || [];
            setAvailableTerms(fetchedTerms);
            setAvailableSemesters([]);
            
            if (fetchedTerms.length > 0 && !selectedTerm) {
              setSelectedTerm(fetchedTerms[0]._id);
            } else if (fetchedTerms.length === 0) {
              setSelectedTerm('');
            }
            setSelectedSemester('');
          }
        } else {
          setAvailableSemesters([]);
          setAvailableTerms([]);
          setSelectedSemester('');
          setSelectedTerm('');
          setIsSemesterBased(false);
        }
      } catch (err) {
        console.error('❌ Error fetching semesters/terms:', err);
        setAvailableSemesters([]);
        setAvailableTerms([]);
        setSelectedSemester('');
        setSelectedTerm('');
      }
    };

    fetchSemestersOrTermsForAcademicYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAcademicYear, academicYears, completedAcademicYears, tenantId]);

  // Sequential selection validation
  useEffect(() => {
    if (!selectedProgramType) {
      setSelectedProgram('');
    }
    if (!selectedProgram) {
      setSelectedAcademicYear('');
    }
    if (!selectedAcademicYear) {
      setSelectedSemester('');
      setSelectedTerm('');
    }
  }, [selectedProgramType, selectedProgram, selectedAcademicYear]);

  // Fetch mappings function wrapped in useCallback
  const fetchMappings = useCallback(async () => {
    if (!tenantId || !selectedProgram || !selectedAcademicYear) {
      setMappings([]);
      setHasExistingMapping(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const programTypeIdForApi = normalizeProgramTypeId(selectedProgramDetails?.programTypeId);

      const params = {
        tenantId,
        programId: selectedProgram,
        programTypeId: programTypeIdForApi,
        academicYearId: selectedAcademicYear
      };

      // Add appropriate context parameter based on academic year type
      if (isSemesterBased) {
        if (!selectedSemester) {
          setError('Please select a semester');
          setLoading(false);
          return;
        }
        params.semesterId = selectedSemester;
      } else {
        if (!selectedTerm) {
          setError('Please select a term');
          setLoading(false);
          return;
        }
        params.termId = selectedTerm;
      }

      const response = await api.get('/api/program-subject-mappings/grouped', { params });
      
      if (response.data.mapping) {
        const mapping = response.data.mapping;
        
        if (!mapping.programId?.name && mapping.programId) {
          mapping.programId = {
            _id: mapping.programId,
            name: getProgramName(mapping.programId),
            code: getProgramCode(mapping.programId)
          };
        }
        
        if (!mapping.programTypeId?.name && mapping.programTypeId) {
          mapping.programTypeId = {
            _id: mapping.programTypeId,
            name: getProgramTypeName(mapping.programTypeId),
            code: getProgramTypeCode(mapping.programTypeId)
          };
        }
        
        setMappings([mapping]);
        setHasExistingMapping(true);
      } else {
        setMappings([]);
        setHasExistingMapping(false);
      }
    } catch (err) {
      console.error('❌ Error fetching mappings:', err);
      if (err.response?.status !== 404) {
        const errorMsg = err.response?.data?.message || 'Failed to load program-subject mappings';
        setError(errorMsg);
      } else {
        setHasExistingMapping(false);
      }
      setMappings([]);
    } finally {
      setLoading(false);
    }
  }, [
    tenantId, 
    selectedProgram, 
    selectedAcademicYear, 
    selectedSemester, 
    selectedTerm,
    isSemesterBased, 
    selectedProgramDetails,
    getProgramName,
    getProgramCode,
    getProgramTypeName,
    getProgramTypeCode
  ]);

  // Show alert modal
  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertModalOpen(true);
  };

  // Delete mapping with confirmation modal
  const confirmDeleteMapping = (mappingId, mappingName) => {
    if (isSelectedAYCompleted) {
      showAlert('Action Not Allowed', 'Cannot delete mapping for completed academic years.');
      return;
    }
    setDeleteMappingId(mappingId);
    setDeleteMappingName(mappingName);
    setDeleteConfirmText('');
    setIsDeleteModalOpen(true);
  };

  const handleDeleteMapping = async () => {
    if (!deleteMappingId) return;

    try {
      await api.delete(`/api/program-subject-mappings/grouped/${deleteMappingId}`, {
        data: { tenantId }
      });

      await fetchMappings();
      showAlert('Success', 'Mapping deleted successfully.');
    } catch (err) {
      console.error('❌ Error deleting mapping:', err);
      showAlert('Error', 'Failed to delete mapping. Please try again.');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteMappingId(null);
      setDeleteMappingName('');
      setDeleteConfirmText('');
    }
  };

  // Handle edit mapping
  const handleEditMapping = (mapping) => {
    if (isSelectedAYCompleted) {
      showAlert('Action Not Allowed', 'Cannot edit mapping for completed academic years.');
      return;
    }
    setEditingMapping(mapping);
    setIsMappingModalOpen(true);
  };

  // Handle create new mapping
  const handleCreateMapping = () => {
    if (isSelectedAYCompleted) {
      showAlert('Action Not Allowed', 'Cannot create new mapping for completed academic years.');
      return;
    }
    setEditingMapping(null);
    setIsMappingModalOpen(true);
  };

  // Handle mapping modal close
  const handleMappingModalClose = () => {
    setIsMappingModalOpen(false);
    setEditingMapping(null);
  };

  // Handle mapping completion
  const handleMappingComplete = () => {
    setIsMappingModalOpen(false);
    setEditingMapping(null);
    fetchMappings();
  };

  // Toggle expanded view for mapping
  const toggleExpandedView = (mappingId) => {
    setExpandedMapping(expandedMapping === mappingId ? null : mappingId);
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedProgramType(selectedProgramTypeId || '');
    setSelectedProgram('');
    setSelectedAcademicYear('');
    setSelectedSemester('');
    setSelectedTerm('');
    setMappings([]);
    setHasExistingMapping(false);
    setError('');
    setEditingMapping(null);
    setExpandedMapping(null);
    setShowCompletedAYs(false);
  };

  // Check if mapping context is complete
  const isMappingContextComplete = selectedProgram && selectedAcademicYear && 
    (isSemesterBased ? selectedSemester : selectedTerm);

  const isViewContextComplete = selectedProgram && selectedAcademicYear && 
    (isSemesterBased ? selectedSemester : selectedTerm);

  // Calculate total credits for a mapping
  const calculateTotalCredits = (mapping) => {
    if (mapping?.totalCredits !== undefined && mapping.totalCredits !== null) {
      return mapping.totalCredits;
    }
    
    if (!mapping?.departmentSubjects) return null;
    
    const calculatedCredits = mapping.departmentSubjects.reduce((total, dept) => {
      return total + (dept.subjects?.reduce((sum, subject) => {
        const credits = subject.credits || subject.subjectId?.credits;
        return sum + (credits || 0);
      }, 0) || 0);
    }, 0);
    
    return calculatedCredits > 0 ? calculatedCredits : null;
  };

  // Calculate total subjects across all departments
  const calculateTotalSubjects = (mapping) => {
    if (!mapping?.departmentSubjects) return 0;
    return mapping.departmentSubjects.reduce((total, dept) => total + (dept.subjects?.length || 0), 0);
  };

  // Calculate subject type distribution
  const calculateSubjectTypeDistribution = (mapping) => {
    if (!mapping?.departmentSubjects) return { core: 0, elective: 0, lab: 0, project: 0, language: 0 };
    
    const distribution = { core: 0, elective: 0, lab: 0, project: 0, language: 0 };
    
    mapping.departmentSubjects.forEach(dept => {
      dept.subjects?.forEach(subject => {
        const subj = subject.subjectId || subject;
        if (subj.isLab) distribution.lab++;
        else if (subj.isElective) distribution.elective++;
        else if (subj.isProject) distribution.project++;
        else if (subj.isLanguage || subj.languageType) distribution.language++;
        else distribution.core++;
      });
    });
    
    return distribution;
  };

  // Get mapping type display (handles both semesters and terms)
  const getMappingTypeDisplay = (mapping) => {
    if (mapping.semesterId) {
      const semesterName = getSemesterName(mapping.semesterId);
      return {
        type: 'semester',
        label: 'Semester',
        value: semesterName,
        color: 'blue',
        gradient: 'from-blue-500 to-cyan-500',
        badgeColor: 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700',
        icon: FiCalendar
      };
    } else if (mapping.termId) {
      const termName = getTermName(mapping.termId);
      return {
        type: 'term',
        label: 'Term',
        value: termName,
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500',
        badgeColor: 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700',
        icon: FiList
      };
    } else {
      return {
        type: 'annual',
        label: 'Annual',
        value: 'Full Academic Year',
        color: 'green',
        gradient: 'from-emerald-500 to-green-500',
        badgeColor: 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700',
        icon: FiAward
      };
    }
  };

  useEffect(() => {
    if (isViewContextComplete) {
      fetchMappings();
    } else {
      setMappings([]);
      setHasExistingMapping(false);
      setEditingMapping(null);
      setExpandedMapping(null);
    }
  }, [isViewContextComplete, fetchMappings]);

  // Filter AY based on showCompletedAYs flag
  const filteredAcademicYears = showCompletedAYs ? completedAcademicYears : academicYears;

  return (
    <div className="space-y-8">
      {/* Modern Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                  <FiBookOpen className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">Program-Subject Mapping</h1>
                  <p className="text-white/90 max-w-2xl">
                    Map and manage subjects across programs, departments, and academic years with visual analytics and smart filtering
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-medium rounded-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  <FiRefreshCw className="h-4 w-4" />
                  Reset Filters
                </button>
                <button
                  type="button"
                  onClick={handleCreateMapping}
                  disabled={!isMappingContextComplete || isSelectedAYCompleted}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiPlus className="h-5 w-5" />
                  {hasExistingMapping ? 'Update Mapping' : 'Create Mapping'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Filter Panel */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg">
              <FiFilter className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Mapping Context
              </h2>
              <p className="text-gray-600">
                Define the academic scope for subject mapping
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Program Type Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                <span className="flex items-center gap-2">
                  <FiGrid className="w-4 h-4" />
                  Program Type
                </span>
              </label>
              <select
                value={selectedProgramType}
                onChange={(e) => {
                  setSelectedProgramType(e.target.value);
                  setSelectedProgram('');
                  setSelectedAcademicYear('');
                  setSelectedSemester('');
                  setSelectedTerm('');
                }}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 focus:bg-white"
              >
                <option value="">All Program Types</option>
                {programTypes.map(type => (
                  <option key={type._id} value={type._id}>
                    {type.name} {type.code && `(${type.code})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Program Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                <span className="flex items-center gap-2">
                  <FiBook className="w-4 h-4" />
                  Program *
                </span>
              </label>
              <select
                value={selectedProgram}
                onChange={(e) => {
                  setSelectedProgram(e.target.value);
                  setSelectedAcademicYear('');
                  setSelectedSemester('');
                  setSelectedTerm('');
                }}
                disabled={!selectedProgramType && programTypes.length > 0}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                required
              >
                <option value="">
                  {!selectedProgramType && programTypes.length > 0 
                    ? 'Select Program Type first' 
                    : filteredPrograms.length === 0
                      ? 'No programs available'
                      : 'Select Program'
                  }
                </option>
                {filteredPrograms.map(program => (
                  <option key={program._id} value={program._id}>
                    {program.name} {program.code && `(${program.code})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Academic Year Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">
                <span className="flex items-center gap-2">
                  <FiCalendar className="w-4 h-4" />
                  Academic Year *
                </span>
              </label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setShowCompletedAYs(false)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-300 ${!showCompletedAYs
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setShowCompletedAYs(true)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-300 ${showCompletedAYs
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Completed
                </button>
              </div>
              <select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  setSelectedSemester('');
                  setSelectedTerm('');
                }}
                disabled={!selectedProgram}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                required
              >
                <option value="">
                  {!selectedProgram ? 'Select Program first' : `Select ${showCompletedAYs ? 'Completed' : 'Active'} Academic Year`}
                </option>
                {filteredAcademicYears
                  .filter(ay => !showCompletedAYs ? ay.status === 'active' : true)
                  .map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name}{ay.label ? ` (${ay.label})` : ''}
                    </option>
                  ))
                }
              </select>
            </div>

            {/* Semester OR Term Filter - Conditionally shown */}
            {isSemesterBased ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  <span className="flex items-center gap-2">
                    <FiClock className="w-4 h-4" />
                    Semester *
                  </span>
                </label>
                <select
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  disabled={!selectedAcademicYear || availableSemesters.length === 0}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">
                    {!selectedAcademicYear 
                      ? 'Select Academic Year first' 
                      : availableSemesters.length === 0 
                        ? 'No semesters available'
                        : 'Select Semester'
                    }
                  </option>
                  {availableSemesters.map(semester => (
                    <option key={semester._id} value={semester._id}>
                      {semester.semesterName || `Semester ${semester.semesterNumber}`}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  <span className="flex items-center gap-2">
                    <FiList className="w-4 h-4" />
                    Term *
                  </span>
                </label>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  disabled={!selectedAcademicYear || availableTerms.length === 0}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">
                    {!selectedAcademicYear 
                      ? 'Select Academic Year first' 
                      : availableTerms.length === 0 
                        ? 'No terms available'
                        : 'Select Term'
                    }
                  </option>
                  {availableTerms.map(term => (
                    <option key={term._id} value={term._id}>
                      {term.termLabel || `Term ${term.termNumber}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Selection Summary */}
          {(selectedProgramType || selectedProgram || selectedAcademicYear) && (
            <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FiActivity className="w-5 h-5 text-blue-600" />
                  Selection Summary
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isSelectedAYCompleted 
                    ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800'
                    : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800'
                }`}>
                  {isSelectedAYCompleted ? 'Completed Year' : 'Active Year'}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {selectedProgramType && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Program Type</div>
                    <div className="text-lg font-semibold text-gray-900">{getProgramTypeName(selectedProgramType)}</div>
                  </div>
                )}
                {selectedProgram && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Program</div>
                    <div className="text-lg font-semibold text-gray-900">{selectedProgramDetails?.name}</div>
                    {selectedProgramDetails?.code && (
                      <div className="text-sm text-gray-600 mt-1">{selectedProgramDetails.code}</div>
                    )}
                  </div>
                )}
                {selectedAcademicYear && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Academic Year</div>
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-semibold text-gray-900">
                        {selectedAcademicYearDetails
                          ? `${selectedAcademicYearDetails.name}${selectedAcademicYearDetails.label ? ` (${selectedAcademicYearDetails.label})` : ''}`
                          : ''}
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedAcademicYearDetails?.isSemesterBased 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {selectedAcademicYearDetails?.isSemesterBased ? 'Semester' : 'Term'}
                      </span>
                    </div>
                  </div>
                )}
                {isSemesterBased && selectedSemester ? (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Semester</div>
                    <div className="text-lg font-semibold text-gray-900">{getSemesterName(selectedSemester)}</div>
                  </div>
                ) : !isSemesterBased && selectedTerm ? (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Term</div>
                    <div className="text-lg font-semibold text-gray-900">{getTermName(selectedTerm)}</div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <FiAlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-red-900">Error Loading Mappings</div>
              <div className="text-red-700 text-sm mt-1">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Mappings Display Section */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl">
                <FiLayers className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Subject Mappings
                </h2>
                <p className="text-gray-600">
                  {hasExistingMapping 
                    ? 'Current program-subject mapping details' 
                    : 'No mapping found for selected context'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-gray-100 rounded-full">
                <span className="text-sm font-medium text-gray-700">
                  {hasExistingMapping ? '1 mapping found' : '0 mappings'}
                </span>
              </div>
              {hasExistingMapping && (
                <>
                  <div className="px-3 py-1 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full">
                    <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1">
                      <FiCheckCircle className="w-3 h-3" />
                      Active
                    </span>
                  </div>
                  {isSelectedAYCompleted && (
                    <div className="px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full">
                      <span className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                        <FiLock className="w-3 h-3" />
                        View Only
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-700 font-medium">Loading subject mappings...</p>
            <p className="text-gray-500 text-sm mt-1">Fetching the latest mapping details</p>
          </div>
        ) : !isViewContextComplete ? (
          <div className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl mb-6">
                <FiLayers className="text-4xl text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Select Context First
                </h3>
                <p className="text-gray-600">
                  Choose a program, academic year, and {isSemesterBased ? 'semester' : 'term'} from the filters above to view or create subject mappings
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-gray-900">{programTypes.length}</div>
                  <div className="text-sm text-gray-600">Program Types</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-bold text-gray-900">{filteredPrograms.length}</div>
                  <div className="text-sm text-gray-600">Available Programs</div>
                </div>
              </div>
            </div>
          </div>
        ) : hasExistingMapping ? (
          <div className="p-6">
            {mappings.map((mapping) => {
              const mappingType = getMappingTypeDisplay(mapping);
              const totalSubjects = calculateTotalSubjects(mapping);
              const totalCredits = calculateTotalCredits(mapping);
              const totalDepartments = mapping.departmentSubjects?.length || 0;
              const subjectDistribution = calculateSubjectTypeDistribution(mapping);
              const isExpanded = expandedMapping === mapping._id;
              
              const programName = getProgramName(mapping.programId);
              const programCode = getProgramCode(mapping.programId);
              const programTypeName = getProgramTypeName(mapping.programTypeId);
              
              return (
                <div key={mapping._id} className="mb-6 last:mb-0">
                  {/* Main Mapping Card */}
                  <div className={`bg-gradient-to-br from-white to-gray-50 border-2 rounded-2xl transition-all duration-300 ${
                    isExpanded 
                      ? 'border-blue-300 shadow-xl' 
                      : 'border-gray-200 hover:border-blue-200 hover:shadow-lg'
                  }`}>
                    <div className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        {/* Left Column - Program Info */}
                        <div className="flex-1">
                          <div className="flex items-start gap-4 mb-4">
                            <div className={`p-3 rounded-xl ${
                              isSelectedAYCompleted
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                                : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                            }`}>
                              <FiBook className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="text-xs font-semibold px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                                  {programTypeName}
                                </span>
                                <span className={mappingType.badgeColor + " inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"}>
                                  <mappingType.icon className="w-3 h-3" />
                                  {mappingType.label}
                                </span>
                                {isSelectedAYCompleted && (
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 rounded-full text-xs font-semibold">
                                    <FiLock className="w-3 h-3" />
                                    View Only
                                  </span>
                                )}
                              </div>
                              <h3 className="text-2xl font-bold text-gray-900 mb-1">{programName}</h3>
                              {programCode && (
                                <p className="text-gray-600">Code: {programCode}</p>
                              )}
                            </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-100">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <FiDatabase className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-blue-900">{totalSubjects}</div>
                                  <div className="text-sm text-blue-700">Total Subjects</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-100">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-100 rounded-lg">
                                  <FiCreditCard className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-emerald-900">
                                    {totalCredits !== null ? totalCredits : 'N/A'}
                                  </div>
                                  <div className="text-sm text-emerald-700">Total Credits</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <FiUsers className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-purple-900">{totalDepartments}</div>
                                  <div className="text-sm text-purple-700">Departments</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-100">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                  <FiTrendingUp className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <div className="text-2xl font-bold text-amber-900">
                                    {Math.round((subjectDistribution.core / totalSubjects) * 100)}%
                                  </div>
                                  <div className="text-sm text-amber-700">Core Subjects</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Actions */}
                        <div className="lg:w-auto">
                          <div className="flex flex-col gap-3">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setIsViewModalOpen(true)}
                                className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300"
                              >
                                <FiEye className="w-4 h-4" />
                                View Details
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleExpandedView(mapping._id)}
                                className="flex-1 lg:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300"
                              >
                                {isExpanded ? (
                                  <>
                                    <FiChevronUp className="w-4 h-4" />
                                    Collapse
                                  </>
                                ) : (
                                  <>
                                    <FiChevronDown className="w-4 h-4" />
                                    Expand
                                  </>
                                )}
                              </button>
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditMapping(mapping)}
                                disabled={isSelectedAYCompleted}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 font-medium rounded-xl transition-all duration-300 ${
                                  isSelectedAYCompleted
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white hover:shadow-lg'
                                }`}
                              >
                                <FiEdit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => confirmDeleteMapping(mapping._id, `${programName} - ${mappingType.value}`)}
                                disabled={isSelectedAYCompleted}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 font-medium rounded-xl transition-all duration-300 ${
                                  isSelectedAYCompleted
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-red-500 to-pink-500 text-white hover:shadow-lg'
                                }`}
                              >
                                <FiTrash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Department Preview */}
                      {!isExpanded && (
                        <div className="mt-6">
                          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                            Departments ({totalDepartments})
                          </h4>
                          <div className="flex flex-wrap gap-3">
                            {mapping.departmentSubjects?.slice(0, 5).map((deptSubj, index) => (
                              <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {deptSubj.departmentId?.name || `Department ${index + 1}`}
                                </span>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {deptSubj.subjects?.length || 0} subjects
                                </span>
                              </div>
                            ))}
                            {mapping.departmentSubjects?.length > 5 && (
                              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                                <span className="text-sm font-medium text-gray-700">
                                  +{mapping.departmentSubjects.length - 5} more
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Expanded View */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gradient-to-b from-gray-50 to-white">
                        <div className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Subject Type Distribution */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <h4 className="text-lg font-bold text-gray-900 mb-4">Subject Type Distribution</h4>
                              <div className="space-y-4">
                                {Object.entries(subjectDistribution).map(([type, count]) => (
                                  count > 0 && (
                                    <div key={type} className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${
                                          type === 'core' ? 'bg-blue-500' :
                                          type === 'elective' ? 'bg-purple-500' :
                                          type === 'lab' ? 'bg-cyan-500' :
                                          type === 'project' ? 'bg-amber-500' :
                                          'bg-emerald-500'
                                        }`}></div>
                                        <span className="font-medium text-gray-700 capitalize">{type}</span>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <div 
                                            className={`h-full ${
                                              type === 'core' ? 'bg-blue-500' :
                                              type === 'elective' ? 'bg-purple-500' :
                                              type === 'lab' ? 'bg-cyan-500' :
                                              type === 'project' ? 'bg-amber-500' :
                                              'bg-emerald-500'
                                            }`}
                                            style={{ width: `${(count / totalSubjects) * 100}%` }}
                                          ></div>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900">
                                          {count} ({Math.round((count / totalSubjects) * 100)}%)
                                        </span>
                                      </div>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>

                            {/* Department Details */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                              <h4 className="text-lg font-bold text-gray-900 mb-4">Department Details</h4>
                              <div className="space-y-4">
                                {mapping.departmentSubjects?.map((deptSubj, index) => {
                                  const deptCredits = deptSubj.subjects?.reduce((sum, subject) => {
                                    return sum + (subject.credits || subject.subjectId?.credits || 0);
                                  }, 0);
                                  
                                  return (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                      <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white rounded-lg">
                                          <FiDatabase className="w-4 h-4 text-gray-600" />
                                        </div>
                                        <div>
                                          <div className="font-medium text-gray-900">
                                            {deptSubj.departmentId?.name || `Department ${index + 1}`}
                                          </div>
                                          <div className="text-sm text-gray-600">
                                            {deptSubj.subjects?.length || 0} subjects
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-4">
                                        {deptCredits > 0 && (
                                          <div className="flex items-center gap-1 bg-emerald-50 px-3 py-1 rounded-full">
                                            <FiCreditCard className="w-3 h-3 text-emerald-600" />
                                            <span className="text-sm font-semibold text-emerald-700">{deptCredits} credits</span>
                                          </div>
                                        )}
                                        <button
                                          type="button"
                                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                          onClick={() => {
                                            // You could implement a department-specific view here
                                          }}
                                        >
                                          View Subjects
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Subject Preview */}
                          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
                            <h4 className="text-lg font-bold text-gray-900 mb-4">Subject Preview</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {mapping.departmentSubjects?.slice(0, 3).map((deptSubj, deptIndex) => (
                                <div key={deptIndex} className="space-y-3">
                                  <h5 className="font-semibold text-gray-900 text-sm">
                                    {deptSubj.departmentId?.name || `Department ${deptIndex + 1}`}
                                  </h5>
                                  <div className="space-y-2">
                                    {deptSubj.subjects?.slice(0, 4).map((subject, subIndex) => (
                                      <div key={subIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-700 truncate">
                                          {subject.subjectId?.name || subject.name}
                                        </span>
                                        <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                          {subject.credits || subject.subjectId?.credits || 0} CR
                                        </span>
                                      </div>
                                    ))}
                                    {deptSubj.subjects?.length > 4 && (
                                      <div className="text-center">
                                        <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                                          + {deptSubj.subjects.length - 4} more subjects
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="p-8 bg-gradient-to-br from-gray-50 to-indigo-50 rounded-2xl mb-6">
                <div className="relative mx-auto w-24 h-24 mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full opacity-20"></div>
                  <div className="absolute inset-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                    <FiLayers className="w-12 h-12 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  No Mapping Found
                </h3>
                <p className="text-gray-600 mb-4">
                  There is no subject mapping for <strong className="text-gray-900">{selectedProgramDetails?.name}</strong> in{' '}
                  <strong className="text-gray-900">
                    {selectedAcademicYearDetails
                      ? `${selectedAcademicYearDetails.name}${selectedAcademicYearDetails.label ? ` (${selectedAcademicYearDetails.label})` : ''}`
                      : ''}
                  </strong>
                  {isSemesterBased && selectedSemester && (
                    <> for <strong className="text-gray-900">{getSemesterName(selectedSemester)}</strong></>
                  )}
                  {!isSemesterBased && selectedTerm && (
                    <> for <strong className="text-gray-900">{getTermName(selectedTerm)}</strong></>
                  )}
                  .
                </p>
                <p className="text-sm text-gray-500 mb-8">
                  {isSelectedAYCompleted 
                    ? 'Completed academic years are view-only and cannot be modified.' 
                    : 'Create a new mapping to assign subjects from multiple departments to this program.'}
                </p>
                {!isSelectedAYCompleted && (
                  <button
                    type="button"
                    onClick={handleCreateMapping}
                    className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                  >
                    <FiPlus className="w-6 h-6" />
                    Create Subject Mapping
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mapping Modal */}
      {isMappingModalOpen && selectedProgramDetails && (
        <ProgramSubjectMapping
          tenantId={tenantId}
          program={selectedProgramDetails}
          open={isMappingModalOpen}
          onClose={handleMappingModalClose}
          onMappingComplete={handleMappingComplete}
          academicYearId={selectedAcademicYear}
          semesterId={isSemesterBased ? selectedSemester : undefined}
          termId={!isSemesterBased ? selectedTerm : undefined}
          existingMapping={editingMapping || (hasExistingMapping ? mappings[0] : null)}
          isEditMode={!!editingMapping}
          isReadOnly={isSelectedAYCompleted}
        />
      )}

      {/* View Modal */}
      {isViewModalOpen && mappings[0] && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden animate-in fade-in duration-300">
            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl">
                  <FiEye className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Mapping Details
                  </h3>
                  <p className="text-gray-600">
                    Comprehensive view of program-subject mapping
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
              <ViewMappedSubjects
                tenantId={tenantId}
                programId={selectedProgram}
                programTypeId={mappings[0].programTypeId || normalizeProgramTypeId(selectedProgramDetails?.programTypeId)}
                academicYearId={selectedAcademicYear}
                academicYearName={getAcademicYearName(selectedAcademicYear)}
                semesterId={mappings[0].semesterId}
                termId={mappings[0].termId}
                mapping={mappings[0]}
                onClose={() => setIsViewModalOpen(false)}
                isReadOnly={isSelectedAYCompleted}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal with type-to-confirm */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteConfirmText('');
        }}
        onConfirm={handleDeleteMapping}
        title="Confirm Deletion"
        message={
          <div>
            <div>
              Are you sure you want to delete the mapping for "<b>{deleteMappingName}</b>"? <br />
              <span className="text-red-700 font-semibold">This action cannot be undone.</span>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Please type <span className="font-bold text-red-600">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                placeholder="Type DELETE to confirm"
                autoFocus
              />
            </div>
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        confirmDisabled={deleteConfirmText !== 'DELETE'}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        title={alertTitle}
        message={alertMessage}
        type="info"
      />
    </div>
  );
};

export default ProgramSubjectMappingSection;